import { Tooltip } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import { openTooltipStyle } from 'features/chatbot/components/conversationMessage/styles'
import { useChatbotAttachments } from 'features/chatbot/contexts/ChatbotAttachmentContext'
import { EmbeddedResource } from 'features/chatbot/hooks/useEmbeddedDocuments'
import {
  createDocumentTab,
  createTimeSeriesTab,
  useKnowledgeDocumentUXContextOptional,
} from 'features/knowledgeDocumentViewer'
import { ChevronDown, Plus, X } from 'lucide-react'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

const SummaryRow = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  background: transparent;
  border: none;
  border-radius: 0.25rem;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.forecast.background1};
  }
`

const SummaryText = styled.span`
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

const ExpandedContainer = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: ${({ $isExpanded }) => ($isExpanded ? '6rem' : '0')};
  overflow: hidden;
  transition:
    max-height 0.25s ease-out,
    opacity 0.2s ease-out;
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};

  &:hover {
    overflow-y: auto;
  }
`

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;

  &:hover {
    background: ${({ theme }) => theme.forecast.background1};
  }
`

const ItemInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  flex: 1;
`

const TypeBadge = styled.span`
  font-size: 0.625rem;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  text-transform: uppercase;
  letter-spacing: 0.025em;
  flex-shrink: 0;
`

const ItemTitle = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ActionButton = styled(CantileverButton)`
  min-height: 0.875rem;
  width: 0.875rem;
  flex-shrink: 0;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  opacity: 0;
  transition: opacity 0.15s ease;

  ${ItemRow}:hover & {
    opacity: 1;
  }

  &:hover {
    color: ${({ theme }) => theme.forecast.secondaryText};
  }
`

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const ChevronIcon = styled.span<{ $isExpanded?: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  transition: transform 0.2s ease-out;
  transform: rotate(${({ $isExpanded }) => ($isExpanded ? '0deg' : '-90deg')});
`

interface ContextCounts {
  documents: number
  tags: number
  alerts: number
  timeWindows: number
}

function getTypeLabel(type: EmbeddedResource['type']): string {
  switch (type) {
    case 'report':
    case 'system_document':
    case 'knowledge_document':
      return 'doc'
    case 'dashboard':
      return 'tag'
    case 'alert':
      return 'alert'
    case 'timeWindow':
      return 'time'
    default:
      return 'item'
  }
}

function formatSummary(counts: ContextCounts): string {
  const parts: string[] = []

  if (counts.documents > 0) {
    parts.push(`${counts.documents} doc${counts.documents > 1 ? 's' : ''}`)
  }
  if (counts.tags > 0) {
    parts.push(`${counts.tags} tag${counts.tags > 1 ? 's' : ''}`)
  }
  if (counts.alerts > 0) {
    parts.push(`${counts.alerts} alert${counts.alerts > 1 ? 's' : ''}`)
  }
  if (counts.timeWindows > 0) {
    parts.push(
      `${counts.timeWindows} time range${counts.timeWindows > 1 ? 's' : ''}`
    )
  }

  return parts.join(', ')
}

export const AttachmentPills: React.FC = () => {
  const { attachments, removeAttachment } = useChatbotAttachments()
  const [isExpanded, setIsExpanded] = useState(false)
  const documentUXContext = useKnowledgeDocumentUXContextOptional()

  const handleOpenAttachment = useCallback(
    (attachment: EmbeddedResource) => {
      if (!documentUXContext) return

      const isDocumentType =
        attachment.type === 'report' ||
        attachment.type === 'system_document' ||
        attachment.type === 'knowledge_document'

      if (isDocumentType) {
        const newTab = createDocumentTab(attachment.id)
        documentUXContext.addTab(newTab)
      } else if (attachment.type === 'dashboard' && attachment.sensorIds) {
        const newTab = createTimeSeriesTab(
          attachment.sensorIds,
          attachment.displayTitle
        )
        documentUXContext.addTab(newTab)
      }
    },
    [documentUXContext]
  )

  const counts = useMemo<ContextCounts>(() => {
    return attachments.reduce(
      (acc, attachment) => {
        switch (attachment.type) {
          case 'report':
          case 'system_document':
          case 'knowledge_document':
            acc.documents++
            break
          case 'dashboard':
            acc.tags++
            break
          case 'alert':
            acc.alerts++
            break
          case 'timeWindow':
            acc.timeWindows++
            break
        }
        return acc
      },
      { documents: 0, tags: 0, alerts: 0, timeWindows: 0 }
    )
  }, [attachments])

  if (attachments.length === 0) {
    return null
  }

  const shouldCollapse = attachments.length > 1
  const showExpanded = !shouldCollapse || isExpanded

  return (
    <Container>
      {shouldCollapse && (
        <SummaryRow
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}>
          <ChevronIcon $isExpanded={isExpanded}>
            <ChevronDown size={12} />
          </ChevronIcon>
          <SummaryText>{formatSummary(counts)}</SummaryText>
        </SummaryRow>
      )}

      <ExpandedContainer $isExpanded={showExpanded}>
        {attachments.map((attachment) => (
          <ItemRow key={attachment.id}>
            <ItemInfo>
              <TypeBadge>{getTypeLabel(attachment.type)}</TypeBadge>
              <ItemTitle title={attachment.displayTitle}>
                {attachment.displayTitle}
              </ItemTitle>
            </ItemInfo>
            <ButtonGroup>
              <Tooltip
                title='Open'
                placement='top'
                color='#1e1e1e'
                arrow={false}
                styles={{ container: openTooltipStyle }}>
                <span>
                  <ActionButton
                    iconOnly
                    ghost
                    onClick={() => handleOpenAttachment(attachment)}
                    aria-label={`Open ${attachment.displayTitle}`}>
                    <Plus size={10} />
                  </ActionButton>
                </span>
              </Tooltip>
              <Tooltip
                title='Remove'
                placement='top'
                color='#1e1e1e'
                arrow={false}
                styles={{ container: openTooltipStyle }}>
                <span>
                  <ActionButton
                    iconOnly
                    ghost
                    onClick={() => removeAttachment(attachment.id)}
                    aria-label={`Remove ${attachment.displayTitle}`}>
                    <X size={10} />
                  </ActionButton>
                </span>
              </Tooltip>
            </ButtonGroup>
          </ItemRow>
        ))}
      </ExpandedContainer>
    </Container>
  )
}
